export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Por qué no se pueden activar, para decírselo al usuario en vez de un
// genérico "no soportado". null = sí se puede.
export function pushUnsupportedReason(): string | null {
  if (isPushSupported()) return null;
  // Fuera de HTTPS (p. ej. http://<IP>:3000 desde el celular) el navegador
  // directamente no expone service workers ni push.
  if (!window.isSecureContext) {
    return 'Las notificaciones solo funcionan con HTTPS (o en localhost). Desde el celular por la IP de la red no se pueden activar: probalo en la app publicada.';
  }
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
    return 'En iPhone, primero instalá la app desde Compartir → Agregar a inicio, y activalas desde ahí.';
  }
  return 'Este navegador no soporta notificaciones.';
}

// Conversión estándar de la VAPID public key (base64url) al formato que
// pide PushManager.subscribe.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

// navigator.serviceWorker.ready no falla nunca: si no hay service worker
// registrado se queda esperando para siempre (y el switch de Ajustes quedaba
// "pensando" sin decir nada). Se le pone un límite con un error claro.
const READY_TIMEOUT_MS = 8000;

async function readyRegistration(): Promise<ServiceWorkerRegistration> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error('El servicio de notificaciones de la app no está activo. Recargá la página e intentá de nuevo.')),
        READY_TIMEOUT_MS
      )
    ),
  ]);
}

export async function getExistingPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const registration = await readyRegistration();
  return registration.pushManager.getSubscription();
}

// Lanza un Error con un mensaje para mostrarle al usuario si no se puede.
export async function subscribeToPush(): Promise<PushSubscription> {
  if (!isPushSupported()) throw new Error('Este navegador no soporta notificaciones.');

  if (Notification.permission === 'denied') {
    throw new Error('Las notificaciones están bloqueadas para este sitio. Habilitalas desde el candado de la barra de direcciones y volvé a intentar.');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Necesitamos tu permiso para mandarte las alertas.');

  const publicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (!publicKey) throw new Error('Falta configurar REACT_APP_VAPID_PUBLIC_KEY en el frontend.');

  const registration = await readyRegistration();
  return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
}

export async function unsubscribeFromPush(): Promise<PushSubscription | null> {
  const sub = await getExistingPushSubscription();
  if (sub) await sub.unsubscribe();
  return sub;
}
